import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Assistant, QiaIcon, QiaWordmark } from "@qitech/dash-agent-sdk";
import { useTranslation } from "react-i18next";
import { requestChatGrant } from "./agentSession";
import "./AgentChat.css";

const GATEWAY_BASE_URL = process.env.REACT_APP_DASH_AGENT_GATEWAY_URL;

const DEFAULT_PANEL_WIDTH = 380;
const MIN_PANEL_WIDTH = 300;
const MAX_PANEL_WIDTH = 700;

/* The dash agent is one assistant for the whole CaaS dashboard — the rule engine is a skill it
   has, not what it is. So this component knows nothing about any screen: a screen mounts it and
   hands over the context its own domain can describe, through the two props below.

   Context reaches the agent in two shapes, mirroring the SDK's two senders:
     chatContext — rides the first submission of each chat, once. For what describes the
                   environment the user is working in and does not change mid-conversation.
     turnContext — rides the next message only. For what the user is looking at right now,
                   which is re-sent whenever it actually changes and skipped when it has not.

   Both are compared by value, so a caller may rebuild the arrays on every render.

   `renderers` is the third thing a screen may hand over and the only one this component passes
   through untouched: a list of {identifier, renderer} the SDK consults when it meets a fenced
   block it does not know. It must keep its identity across renders, which is the caller's to
   guarantee — the SDK carries it down a context.

   `attachments` is the fourth, and it is a controlled list: what the person pointed at on the
   screen, staged above the input. The SDK draws each one through the renderer registered for its
   identifier and asks for changes through `onAttachmentsChange` — on the remove button, and empty
   once a send has taken them. On send it travels inside the message text, so it is drawn again
   whenever the conversation is reopened.

   `open` is optional, and handing it over changes who owns the panel. Left out, the SDK draws its
   own floating launcher and opens itself. Supplied, the screen owns the state and the launcher is
   hidden — because a screen that already has a toolbar would otherwise show two ways to open the
   same panel. Either way `onPanelOpenChange` reports every change, including the panel's own close
   button, so a screen that puts itself in a mode while the chat is open can come back out.

   `alwaysNewChat` opens into an empty conversation instead of resuming the last one. It only
   applies to a fresh mount: once a message has been sent the chat is materialized, and closing
   and reopening the panel returns to it. */
function AgentChat({
    chatContext,
    turnContext,
    renderers,
    attachments,
    onAttachmentsChange,
    open,
    onPanelOpenChange,
    alwaysNewChat,
    title,
    greeting,
    emptySubtitle,
    suggestions
}) {
    const { t } = useTranslation();

    const [reservedWidth, setReservedWidth] = useState(0);
    const [animateReflow, setAnimateReflow] = useState(true);

    /* The SDK hands these senders back once it has mounted, so they are state rather than refs —
       the effects below must re-run the moment they arrive. */
    const [queueTurnContext, setQueueTurnContext] = useState(null);
    const [queueChatContext, setQueueChatContext] = useState(null);

    const handleContextSender = useCallback((sender) => setQueueTurnContext(() => sender), []);
    const handleChatContextSender = useCallback((sender) => setQueueChatContext(() => sender), []);

    const turnContextJson = useMemo(
        () => (turnContext && turnContext.length > 0 ? JSON.stringify(turnContext) : null),
        [turnContext]
    );
    const chatContextJson = useMemo(
        () => (chatContext && chatContext.length > 0 ? JSON.stringify(chatContext) : null),
        [chatContext]
    );

    useEffect(() => {
        if (!queueTurnContext || !turnContextJson) return;
        queueTurnContext(JSON.parse(turnContextJson));
    }, [queueTurnContext, turnContextJson]);

    useEffect(() => {
        if (!queueChatContext || !chatContextJson) return;
        queueChatContext(JSON.parse(chatContextJson));
    }, [queueChatContext, chatContextJson]);

    const handleWidthChange = useCallback((width, options) => {
        setReservedWidth(width);
        setAnimateReflow(!options || options.animate !== false);
    }, []);

    const hostOwnsPanel = typeof open === "boolean";

    if (!GATEWAY_BASE_URL) return null;

    return (
        <React.Fragment>
            {/* The panel paints as a fixed rail, so the host reserves its width here and the
                screen behind it reflows instead of being covered. */}
            <div
                style={{
                    width: reservedWidth + "px",
                    flexShrink: 0,
                    transition: animateReflow ? "width .2s ease" : "none"
                }}
            />
            <Assistant
                gatewayBaseUrl={GATEWAY_BASE_URL}
                getChatGrant={requestChatGrant}
                title={title || t("Assistente")}
                greeting={greeting}
                emptySubtitle={emptySubtitle}
                suggestions={suggestions}
                displayMode="push"
                side="right"
                defaultWidth={DEFAULT_PANEL_WIDTH}
                minWidth={MIN_PANEL_WIDTH}
                maxWidth={MAX_PANEL_WIDTH}
                resizable
                onWidthChange={handleWidthChange}
                renderers={renderers}
                attachments={attachments}
                onAttachmentsChange={onAttachmentsChange}
                open={hostOwnsPanel ? open : undefined}
                hideLauncher={hostOwnsPanel}
                onOpenChange={onPanelOpenChange}
                alwaysNewChat={alwaysNewChat}
                onContextSender={handleContextSender}
                onChatContextSender={handleChatContextSender}
            />
        </React.Fragment>
    );
}

/* The button that opens the panel, for a screen that owns the panel's state and wants it among
   its own buttons rather than floating over the corner. It carries the SDK's mark instead of the
   host's button style: the assistant is a guest on this screen, not one of its actions.

   It draws nothing while the panel is open — the panel has its own close button, and two ways to
   close the same thing, one of them underneath the panel itself, is one too many. */
export function AgentChatLauncher({ open, onOpenChange, title }) {
    const { t } = useTranslation();

    if (!GATEWAY_BASE_URL || open) return null;

    return (
        <button
            type="button"
            className="qia-launcher"
            onClick={() => onOpenChange(true)}
            aria-label={title || t("Assistente")}
        >
            <QiaIcon size={22}/>
            <span className="qia-launcher-word"><QiaWordmark size={14}/></span>
        </button>
    );
}

export default AgentChat;
